import { useQuery } from '@tanstack/react-query';

import {
  type MaintenanceConfigValue,
  type SystemConfig,
  SystemConfigKey,
  maintenanceConfigValueSchema,
} from '../../entity/api/system-config';
import { getQueryClient } from '../../sdk/request';
import { getAllSystemConfigs } from '../../sdk/system-config';

// Query Keys
export const systemConfigQueryKeys = {
  all: ['system-configs', 'all'] as const,
} as const;

// Query: Get All System Configs
export function useSystemConfigs() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return useQuery({
    queryKey: systemConfigQueryKeys.all,
    queryFn: async () => {
      const response = await getAllSystemConfigs();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - allows periodic refetching for maintenance updates
    gcTime: Infinity, // Never garbage collect - keep in cache for entire app session
    refetchOnWindowFocus: true, // Refetch on window focus to catch maintenance updates
    refetchOnMount: false, // Use cache if available
    retry: 2, // Retry twice on failure
    enabled: true, // Always enabled - public endpoint
  });
}

// Helper: Get system configs from cache (imperative access)
export function getSystemConfigsFromCache(): SystemConfig[] | undefined {
  const queryClient = getQueryClient();
  return queryClient.getQueryData<SystemConfig[]>(systemConfigQueryKeys.all);
}

// Helper: Prefetch system configs
export async function prefetchSystemConfigs(): Promise<void> {
  const queryClient = getQueryClient();

  await queryClient.prefetchQuery({
    queryKey: systemConfigQueryKeys.all,
    queryFn: async () => {
      const response = await getAllSystemConfigs();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - allows periodic refetching for maintenance updates
    gcTime: Infinity, // Never garbage collect - keep in cache for entire app session
  });
}

// Helper: Invalidate system configs queries
export function invalidateSystemConfigs(): void {
  const queryClient = getQueryClient();
  void queryClient.invalidateQueries({ queryKey: systemConfigQueryKeys.all });
}

// Helper: Get maintenance config from system configs
export function getMaintenanceConfig(): MaintenanceConfigValue | null {
  const configs = getSystemConfigsFromCache();
  if (configs == null) {
    return null;
  }

  const maintenanceConfig = configs.find(function (config) {
    return config.key === SystemConfigKey.Maintenance;
  });

  if (maintenanceConfig == null) {
    return null;
  }

  // Check if value is undefined or empty
  if (maintenanceConfig.value == null || maintenanceConfig.value === 'undefined') {
    return null;
  }

  try {
    let parsedValue: unknown;

    // Handle both string (JSON string) and object (already parsed by API)
    if (typeof maintenanceConfig.value === 'string') {
      // Check if string is empty
      if (maintenanceConfig.value.trim() === '') {
        return null;
      }
      // Parse JSON string
      parsedValue = JSON.parse(maintenanceConfig.value);
    } else if (typeof maintenanceConfig.value === 'object') {
      // Already an object, use directly
      parsedValue = maintenanceConfig.value;
    } else {
      return null;
    }

    // Validate the parsed/object value
    const validatedValue = maintenanceConfigValueSchema.parse(parsedValue);
    return validatedValue;
  } catch (_err) {
    return null;
  }
}
